import {
  DataTexture,
  EquirectangularReflectionMapping,
  PMREMGenerator,
  Scene,
  Texture,
  WebGLRenderer,
} from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { fetchWithProgress } from '../util/fetchWithProgress';
import type { ProgressCallback } from '../machine/MachineLoader';

/**
 * Load an HDRI as the scene's environment map (for chrome / glass reflections).
 * The background stays solid white — HDRI is reflections-only.
 *
 * Streams the .hdr file with byte-level progress so the caller can drive a loading bar,
 * then runs RGBELoader.parse() on the buffer in-memory.
 */
export async function loadEnvironment(
  scene: Scene,
  renderer: WebGLRenderer,
  hdrUrl: string,
  onProgress: ProgressCallback = () => {},
): Promise<Texture> {
  const buffer = await fetchWithProgress(hdrUrl, onProgress);

  const loader = new RGBELoader();
  // RGBELoader.parse returns the raw decoded fields; we construct the DataTexture ourselves
  // (the same way loader.load does internally). Format/colorSpace default to what HDR data
  // needs (RGBAFormat, NoColorSpace) on the DataTexture constructor.
  const tex = loader.parse(buffer);
  const hdr = new DataTexture(tex.data, tex.width, tex.height);
  hdr.type = tex.type;
  hdr.needsUpdate = true;
  hdr.mapping = EquirectangularReflectionMapping;

  const pmrem = new PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const envMap = pmrem.fromEquirectangular(hdr).texture;
  scene.environment = envMap;
  hdr.dispose();
  pmrem.dispose();
  return envMap;
}
